/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ServiceService from '../../services/service.service';

// Tipos para las conversaciones
interface Conversation {
  id: string;
  title: string;
  subtitle: string; // Añadimos el subtítulo para el servicio
  type: string;
  message: string;
  date: string;
  saved: boolean;
  status?: string;
  originalRequest?: any; // Para guardar la solicitud original completa
}

interface ConversationHistoryProps {
  onSelectConversation?: (conversation: any) => void;
  onNewConversation?: () => void;
  isMobile?: boolean;
}

const ConversationHistory: React.FC<ConversationHistoryProps> = ({ onSelectConversation, onNewConversation, isMobile = false }) => {
  // Estado para el filtro activo: 'all' o 'saved'
  const [activeTab, setActiveTab] = useState<'chats' | 'saved'>('chats');
  // Estado para el término de búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  // Estado para las conversaciones/solicitudes de servicio
  const [conversations, setConversations] = useState<Conversation[]>([]);
  // Estado para indicar si está cargando
  const [isLoading, setIsLoading] = useState(true);
  // Estado para manejar errores
  const [error, setError] = useState<string | null>(null);

  // Obtener el contexto de autenticación
  const { currentUser, userInfo } = useAuth();

  // Cargar las solicitudes de servicio del usuario
  useEffect(() => {
    const fetchUserServiceRequests = async () => {
      // Intentar obtener el userId del localStorage o del token JWT
      const userToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const userId = localStorage.getItem('user_id');

      if (!userId && !userToken) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Obtener el ID del usuario actual del contexto de autenticación
        let userIdToUse = userId;

        // Si no tenemos el ID en localStorage, intentar obtenerlo del contexto de autenticación
        if (!userIdToUse && currentUser && currentUser.uid) {
          userIdToUse = currentUser.uid;
        }

        // userInfo no tiene una propiedad id, pero podemos intentar obtener el perfil completo del usuario
        // que sí contiene el ID interno del backend
        if (!userIdToUse && userInfo && userInfo.email) {
          try {
            const { UserService } = await import('../../services/user.service');
            const userProfile = await UserService.getCurrentUserProfile();

            if (userProfile && userProfile.id) {
              userIdToUse = userProfile.id;
              localStorage.setItem('user_id', userIdToUse);
            }
          } catch (error) {
            console.error('Error al obtener el perfil del usuario:', error);
          }
        }

        // Si aún no tenemos un ID válido, intentar extraerlo del token JWT
        if (!userIdToUse && userToken) {
          try {
            // Intentar decodificar el token JWT para extraer el ID del usuario
            // Los tokens JWT tienen formato: header.payload.signature
            const tokenParts = userToken.split('.');
            if (tokenParts.length === 3) {
              // Decodificar la parte del payload (segunda parte)
              const payload = JSON.parse(atob(tokenParts[1]));

              // Buscar el ID del usuario en el payload (puede estar en sub, id, userId, etc.)
              // Priorizar el ID interno del backend, no el googleId
              if (payload.id) {
                userIdToUse = payload.id;
              } else if (payload.userId) {
                userIdToUse = payload.userId;
              } else if (payload.sub) {
                userIdToUse = payload.sub;
              }

              // Si encontramos el ID, guardarlo en localStorage para futuras consultas
              if (userIdToUse) {
                localStorage.setItem('user_id', userIdToUse);
              }
            }
          } catch (error) {
            console.error('Error al intentar extraer el ID del usuario del token JWT:', error);
          }
        }

        // Si aún no tenemos un ID válido, mostrar error y salir
        if (!userIdToUse) {
          setError('No se pudo obtener el ID de usuario. Por favor, inicia sesión nuevamente.');
          setIsLoading(false);
          return;
        }

        // Obtener las solicitudes de servicio del usuario
        const serviceRequests = await ServiceService.getUserServiceRequests(userIdToUse);

        // Transformar las solicitudes de servicio al formato de conversaciones
        const formattedConversations: Conversation[] = serviceRequests.map(request => {
          // Determinar si está guardado (por ahora, asumimos que todos los con estado 'Completed' están guardados)
          // Status 0 = Pending, 1 = In Progress, 2 = Completed
          const isSaved = request.status === 2;

          // Formatear la fecha relativa (ej: "Hace 2 horas")
          let formattedDate = 'Fecha desconocida';
          if (request.created) {
            try {
              const date = new Date(request.created);
              const now = new Date();

              // Calcular la diferencia en milisegundos
              const diffMs = now.getTime() - date.getTime();

              // Convertir a diferentes unidades de tiempo
              const diffMinutes = Math.floor(diffMs / (1000 * 60));
              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

              // Formatear de manera personalizada
              if (diffMinutes < 1) {
                formattedDate = 'Hace un momento';
              } else if (diffMinutes < 60) {
                formattedDate = `Hace ${diffMinutes} minutos`;
              } else if (diffHours < 24) {
                formattedDate = `Hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
              } else if (diffDays < 30) {
                formattedDate = `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
              } else {
                // Para fechas más antiguas, mostrar la fecha exacta
                formattedDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
              }
            } catch (e) {
            }
          }

          // Extraer la conversación para mostrarla
          let conversationSummary = 'Sin conversación disponible';
          if (request.conversationHistory && request.conversationHistory.length >= 2) {
            // Tomamos el primer mensaje del usuario y la primera respuesta del asistente
            const userMessage = request.conversationHistory.find(msg => msg.role === 'user')?.content || '';
            const assistantMessage = request.conversationHistory.find(msg => msg.role === 'assistant')?.content || '';

            // Combinamos los mensajes y limitamos a dos líneas con puntos suspensivos
            conversationSummary = userMessage.substring(0, 50) +
              (userMessage.length > 50 ? '...' : '') +
              (assistantMessage ? '\n' + assistantMessage.substring(0, 50) +
                (assistantMessage.length > 50 ? '...' : '') : '');
          }

          return {
            id: request.id,
            title: request.categoryName || 'General', // Título es la categoría
            subtitle: request.serviceTitle || 'Solicitud sin nombre', // Subtítulo es el servicio
            type: request.categoryName || 'general', // Mantenemos la categoría sin toLowerCase
            message: conversationSummary, // Usamos el resumen de la conversación
            date: formattedDate,
            saved: isSaved,
            status: request.status === 0 ? 'Pending' : request.status === 1 ? 'In Progress' : 'Completed',
            originalRequest: request // Guardamos la solicitud original completa
          };
        });

        setConversations(formattedConversations);
      } catch (err) {
        setError('No se pudieron cargar las solicitudes. Intenta de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserServiceRequests();
  }, []);

  // Determinar qué conversaciones mostrar
  const conversationsToUse = conversations;

  // Filtrar conversaciones según la pestaña activa y el término de búsqueda
  const filteredConversations = conversationsToUse.filter(conv => {
    const matchesTab = activeTab === 'chats' || (activeTab === 'saved' && conv.saved);
    const matchesSearch = searchTerm === '' ||
      conv.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.message.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesTab && matchesSearch;
  });

  // Contar las conversaciones para cada pestaña
  const chatCount = conversationsToUse.length;
  const savedCount = conversationsToUse.filter(conv => conv.saved).length;

  return (
    <div className="bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark h-full w-80 flex flex-col border-r border-xervix-bg-primary dark:border-xervix-bg-primary-dark">
      {/* Encabezado */}
      <div className="pl-4 pr-12 pt-4 md:p-4 flex items-start justify-between">
        <h2 className="text-xl font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">Mis solicitudes</h2>
        <button 
          className="w-6 h-6 md:w-8 md:h-8 bg-xervix-primary rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors"
          onClick={() => {
            if (onNewConversation) {
              // Mostrar confirmación si hay una conversación activa
              if (window.confirm('¿Deseas iniciar una nueva conversación? La conversación actual se perderá si no ha sido guardada.')) {
                onNewConversation();
              }
            }
          }}
          title="Iniciar nueva conversación"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* Pestañas */}
      <div className="p-3 ">
        <div className="flex rounded-lg overflow-hidden bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark p-2 ">
          <button
            className={`rounded-lg flex-1 py-3 px-4 text-center text-[12px] font-medium transition-colors ${activeTab === 'chats' ? 'bg-xervix-bg-primary dark:bg-xervix-bg-primary-dark text-xervix-primary' : 'text-xervix-text-muted dark:text-xervix-text-muted-dark hover:text-xervix-text-secondary dark:hover:text-xervix-text-secondary-dark'}`}
            onClick={() => setActiveTab('chats')}
          >
            <span className="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              CHATS
              <span className={`ml-2 ${activeTab === 'chats' ? 'bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark text-xervix-primary' : 'bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300'} text-[12px] px-2 py-0.5 rounded-full`}>{chatCount}</span>
            </span>
          </button>
          <button
            className={`rounded-lg flex-1 py-3 px-4 text-center text-[12px] font-medium transition-colors ${activeTab === 'saved' ? 'bg-xervix-bg-primary dark:bg-xervix-bg-primary-dark text-xervix-primary' : 'text-xervix-text-muted dark:text-xervix-text-muted-dark hover:text-xervix-text-secondary dark:hover:text-xervix-text-secondary-dark'}`}
            onClick={() => setActiveTab('saved')}
          >
            <span className="flex items-center justify-center">
              SAVED
              <span className={`ml-2 ${activeTab === 'saved' ? 'bg-xervix-primary/30 text-xervix-primary' : 'bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300'} text-[12px] px-2 py-0.5 rounded-full`}>{savedCount}</span>
            </span>
          </button>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="p-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar"
            className="w-full bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark text-xervix-text-secondary dark:text-xervix-text-secondary-dark py-2 pl-10 pr-4 rounded-md focus:outline-none focus:ring-1 focus:ring-xervix-primary"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-xervix-text-muted dark:text-xervix-text-muted-dark absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchTerm && (
            <button
              className="absolute right-3 top-2.5 text-xervix-text-muted dark:text-xervix-text-muted-dark hover:text-xervix-text-secondary dark:hover:text-xervix-text-secondary-dark"
              onClick={() => setSearchTerm('')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="mt-2 flex items-center justify-end">
            <button className="p-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-xervix-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Lista de conversaciones */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full p-4">
            <div className="w-8 h-8 border-2 border-xervix-primary border-t-transparent rounded-full animate-spin mb-2"></div>
            <p className="text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark">Cargando solicitudes...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full p-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-xervix-text-muted dark:text-xervix-text-muted-dark mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark text-center">
              {searchTerm ? 'No se encontraron solicitudes que coincidan con tu búsqueda.' : 'No tienes solicitudes de servicio actualmente.'}
            </p>
            {!searchTerm && (
              <p className="mt-2 text-xs text-xervix-text-muted dark:text-xervix-text-muted-dark text-center">
                Puedes crear una nueva solicitud conversando con nuestro asistente.
              </p>
            )}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-xervix-text-muted dark:text-xervix-text-muted-dark mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark text-center">
              {searchTerm ? 'No se encontraron solicitudes que coincidan con tu búsqueda.' : 'No tienes solicitudes de servicio actualmente.'}
            </p>
            {!searchTerm && (
              <p className="mt-2 text-xs text-xervix-text-muted dark:text-xervix-text-muted-dark text-center">
                No tienes Solicitudes creadas.
              </p>
            )}
          </div>
        ) : (
          filteredConversations.map((conversation) => (
            <div
              key={conversation.id}
              className="p-4 hover:bg-xervix-bg-tertiary dark:hover:bg-xervix-bg-tertiary-dark cursor-pointer w-[100%]"
              onClick={() => onSelectConversation && onSelectConversation(conversation.originalRequest)}
            >
              <div className="flex items-start mb-1 w-full">
                <span className={`w-2 h-2 rounded-full mt-2 ${conversation.type === 'Carpinteria' ? 'bg-red-400' :
                  conversation.type === 'Plomeria' ? 'bg-blue-400' :
                    conversation.type === 'Mantenimiento' ? 'bg-green-400' :
                      conversation.type === 'Jardineria' ? 'bg-yellow-400' :
                        conversation.type === 'Carpintería' ? 'bg-red-400' : 'bg-purple-400'}`}>
                </span>

                <div className=' w-[100%]'>
                  <div className="ml-2 flex justify-between items-stretch">
                    <h3 className=" text-md font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark">{conversation.title}</h3>
                    <span className="ml-auto text-xs text-xervix-text-muted dark:text-xervix-text-muted-dark">{conversation.date}</span>
                  </div>

                  <p className="ml-2 text-sm font-medium text-xervix-text-secondary dark:text-xervix-text-secondary-dark w-full mb-1 whitespace-pre-line line-clamp-1">{conversation.subtitle}</p>
                  <p className="ml-2 text-xs text-xervix-text-muted dark:text-xervix-text-muted-dark whitespace-pre-line line-clamp-2">{conversation.message}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationHistory;
